import { Button, Icons, Input, Sheet, Stack, Tooltip, Typography } from '@healthinal/ui';
import type { LabOrderDto } from '../../../../api/generated/model/labOrderDto.ts';
import { useNavigate } from '@tanstack/react-router';
import { TracingContactDetailsDtoAssessmentStatus } from '../../../../api/generated/model';
import { LabOrderDtoInterpretation } from '../../../../api/generated/model/labOrderDtoInterpretation.ts';
import { LabOrderResultDtoType } from '../../../../api/generated/model/labOrderResultDtoType.ts';
import { LabOrderDtoType } from '../../../../api/generated/model/labOrderDtoType.ts';
import type { ScreeningTracingIndividualDto } from '../../../../api/generated/screening-tracing-controller/screening-tracing-controller.ts';
import { TooltipCell } from '../../../../components/table/TooltipCell.tsx';
import { PATIENT_TABLE_CELL_HEIGHT } from '../../../../theme.ts';
import { getTracingContactAssessmentStatusName } from '../../../../utils/contactTracing.ts';
import { formatDate } from '../../../../utils/date.ts';
import { getLatestLabOrder } from '../../../../utils/labOrder.ts';
import { PatientTable } from '../../../patients/components/tables/PatientTable.tsx';
import { useScreeningIndividualsTable } from './useScreeningIndividualsTable.ts';

function getMostRecentLabOrder(
  individual: ScreeningTracingIndividualDto,
  type: LabOrderDtoType,
): LabOrderDto | undefined {
  return getLatestLabOrder(individual.labOrders.filter((order) => order.type === type));
}

function getInterpretationDisplay(interpretation: LabOrderDtoInterpretation): string {
  const map: Record<LabOrderDtoInterpretation, string> = {
    [LabOrderDtoInterpretation.POSITIVE]: 'P',
    [LabOrderDtoInterpretation.NEGATIVE]: 'N',
    [LabOrderDtoInterpretation.INDETERMINATE]: 'U',
    [LabOrderDtoInterpretation.NOT_EXECUTED]: 'Nicht durchgeführt',
    [LabOrderDtoInterpretation.PENDING]: 'Ausstehend',
    [LabOrderDtoInterpretation.NONE]: 'Keine Angabe',
  };
  return map[interpretation];
}

function getXrayResultDisplay(individual: ScreeningTracingIndividualDto): string | undefined {
  const xray = getMostRecentLabOrder(individual, LabOrderDtoType.XRAY);
  if (!xray) return undefined;

  const finding = xray.results.find((r) => r.type === 'XRAY_FINDING');
  if (!finding) return undefined;

  const map: Record<string, string> = {
    NORMAL: 'N',
    ABNORMAL_TB_SUSPICION: 'A+',
    ABNORMAL_TB_UNLIKELY: 'A-',
  };
  return map[finding.result] ?? finding.result;
}

function getTbiDisplay(individual: ScreeningTracingIndividualDto): { text: string; color: string } | undefined {
  const { referralDate, therapyStartDate, therapyEndDate } = individual;

  if (!referralDate) return undefined;

  if (referralDate && (!therapyStartDate || !therapyEndDate)) {
    if (therapyStartDate && !therapyEndDate) {
      return { text: 'Gestartet', color: '#e6a817' };
    }
    return { text: 'überwiesen', color: '#888' };
  }

  if (referralDate && therapyStartDate && therapyEndDate) {
    return { text: 'beendet', color: '#1b8a3d' };
  }

  return undefined;
}

function getLatestLabOrderDate(labOrders: LabOrderDto[]) {
  const latestLabOrder = getLatestLabOrder(labOrders);

  if (!latestLabOrder) return undefined;

  if (
    latestLabOrder.interpretation !== LabOrderDtoInterpretation.PENDING &&
    latestLabOrder.interpretation !== LabOrderDtoInterpretation.NONE
  ) {
    return formatDate(latestLabOrder.samplingDate);
  }

  if (latestLabOrder.results.find((result) => result.type === LabOrderResultDtoType.XRAY_FINDING)) {
    return formatDate(latestLabOrder.samplingDate);
  }

  return (
    <Tooltip title={'Noch kein Testresultat vorhanden'}>
      <Typography color={'warning'} endDecorator={<Icons.ErrorOutline color={'warning'} size={'sm'} />}>
        {formatDate(latestLabOrder.samplingDate)}
      </Typography>
    </Tooltip>
  );
}

function getCurrentStatus(individual: ScreeningTracingIndividualDto) {
  const assessmentStatus = individual.assessmentStatus;

  if (!assessmentStatus) return undefined;

  switch (assessmentStatus) {
    case TracingContactDetailsDtoAssessmentStatus.PENDING:
      return (
        <Tooltip title={getTracingContactAssessmentStatusName(assessmentStatus)}>
          <Stack alignContent={'center'}>
            <Icons.ErrorOutline size={'sm'} color={'warning'} />
          </Stack>
        </Tooltip>
      );
    case TracingContactDetailsDtoAssessmentStatus.NOT_NEEDED:
      return (
        <Tooltip title={getTracingContactAssessmentStatusName(assessmentStatus)}>
          <Stack alignContent={'center'}>
            <Icons.RemoveCircleOutline size={'sm'} color={'success'} />
          </Stack>
        </Tooltip>
      );
    case TracingContactDetailsDtoAssessmentStatus.WAITING_PERIOD:
      return (
        <Tooltip title={getTracingContactAssessmentStatusName(assessmentStatus)}>
          <Stack alignContent={'center'}>
            <Icons.ChangeCircleOutlined size={'sm'} />
          </Stack>
        </Tooltip>
      );
    case TracingContactDetailsDtoAssessmentStatus.RESULT_POSITIVE:
    case TracingContactDetailsDtoAssessmentStatus.RESULT_NEGATIVE:
    case TracingContactDetailsDtoAssessmentStatus.RESULT_INDETERMINATE:
      return (
        <Tooltip title={`Resultat vorhanden: ${getTracingContactAssessmentStatusName(assessmentStatus)}`}>
          <Stack alignContent={'center'}>
            <Icons.CheckCircleOutline color={'success'} size={'sm'} />
          </Stack>
        </Tooltip>
      );
    default:
      return (
        <Tooltip title={getTracingContactAssessmentStatusName(assessmentStatus)}>
          <Stack alignContent={'center'}>
            <Icons.HelpOutline size={'sm'} />
          </Stack>
        </Tooltip>
      );
  }
}

export function ScreeningIndividualsTable({ screeningCaseId }: { screeningCaseId: string }) {
  const navigate = useNavigate();
  const {
    individuals,
    isLoading,
    page,
    totalPages,
    search,
    handleSearchChange,
    handlePrevPage,
    handleNextPage,
  } = useScreeningIndividualsTable(screeningCaseId);

  return (
    <Stack gap={2} sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="flex-end">
        <Input
          placeholder="Suche..."
          value={search}
          onChange={(event) => handleSearchChange(event.target.value)}
          sx={{ width: 260, maxWidth: '100%', flexShrink: 0 }}
        />
      </Stack>

      <Sheet sx={{ px: 2, py: 1, borderRadius: 5, minWidth: 0 }}>
        <Sheet sx={{ overflow: 'auto', width: '100%', minWidth: 0 }}>
          <PatientTable stickyHeader borderAxis="xBetween" noWrap hoverRow>
            <thead>
              <tr>
                <th style={{ width: 180, minWidth: 180 }}>Name</th>
                <th style={{ width: 160, minWidth: 160 }}>Vorname</th>
                <th style={{ width: 125 }}>Geb.</th>
                <th style={{ width: 130 }}>Test erfolgt</th>
                <th style={{ width: 150, minWidth: 150 }}>Mx</th>
                <th style={{ width: 150, minWidth: 150 }}>QFT</th>
                <th style={{ width: 150, minWidth: 150 }}>T-Spot</th>
                <th style={{ width: 85 }}>Rx</th>
                <th style={{ width: 150, minWidth: 150 }}>S</th>
                <th style={{ width: 100 }}>TBI</th>
                <th style={{ width: 85 }}>Status</th>
              </tr>
            </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', height: PATIENT_TABLE_CELL_HEIGHT }}>
                  <Typography level="body-sm">Lade Daten...</Typography>
                </td>
              </tr>
            ) : individuals.length === 0 ? (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', height: PATIENT_TABLE_CELL_HEIGHT }}>
                  <Typography level="body-sm">Keine Kontaktpersonen gefunden</Typography>
                </td>
              </tr>
            ) : (
              individuals.map((individual) => (
                <tr
                  key={individual.id}
                  onClick={() =>
                    navigate({
                      to: '/screening/$screeningId/individuals/$individualId',
                      params: { screeningId: individual.screeningCaseId, individualId: individual.id },
                    })
                  }
                  style={{ cursor: 'pointer' }}>
                  <ScreeningIndividualsTableData individual={individual} />
                </tr>
              ))
            )}
          </tbody>
        </PatientTable>
        </Sheet>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Typography level="body-sm">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span>Seite</span>
              <span translate="no" className="notranslate">
                {totalPages === 0 ? 0 : page + 1}
              </span>
              <span>von</span>
              <span translate="no" className="notranslate">
                {totalPages}
              </span>
            </span>
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" disabled={page === 0 || isLoading} onClick={handlePrevPage}>
              Zurück
            </Button>
            <Button variant="outlined" disabled={page + 1 >= totalPages || isLoading} onClick={handleNextPage}>
              Weiter
            </Button>
          </Stack>
        </Stack>
      </Sheet>
    </Stack>
  );
}

function ScreeningIndividualsTableData({ individual }: { individual: ScreeningTracingIndividualDto }) {
  const mx = getMostRecentLabOrder(individual, LabOrderDtoType.TUBERCULIN);
  const qft = getMostRecentLabOrder(individual, LabOrderDtoType.BLOOD_QUANTIFERON);
  const tspot = getMostRecentLabOrder(individual, LabOrderDtoType.BLOOD_TSPOT);
  const sputum = getMostRecentLabOrder(individual, LabOrderDtoType.SPUTUM);
  const tbi = getTbiDisplay(individual);

  return (
    <>
      <TooltipCell>{individual.lastName}</TooltipCell>
      <TooltipCell>{individual.firstName}</TooltipCell>
      <td>{formatDate(individual.birthDate)}</td>
      <td>{getLatestLabOrderDate(individual.labOrders)}</td>
      <td>{mx ? getInterpretationDisplay(mx.interpretation) : ''}</td>
      <td>{qft ? getInterpretationDisplay(qft.interpretation) : ''}</td>
      <td>{tspot ? getInterpretationDisplay(tspot.interpretation) : ''}</td>
      <td>{getXrayResultDisplay(individual) ?? ''}</td>
      <td>{sputum ? getInterpretationDisplay(sputum.interpretation) : ''}</td>
      <td>{tbi ? <span style={{ color: tbi.color }}>{tbi.text}</span> : ''}</td>
      <td>{getCurrentStatus(individual)}</td>
    </>
  );
}
